// Minutes to plan (/minutes-to-plan?projectId=) — paste or upload meeting
// minutes, review what the parser proposes, and put it on the plan.
//
// Three steps, deliberately: minutes are ambiguous prose, so nothing is
// created until a person has looked at the list and ticked it. Step 2 is the
// point at which someone takes responsibility for what lands on the schedule.
//
//   1. pick the plan item the meeting belongs to, paste or upload
//   2. review — every row editable, unwanted rows unticked. Owner is picked
//      from the project roster: the parser reads a NAME out of the prose
//      ("@Diego"), and a name is not a person, so the match is made here in
//      front of somebody rather than guessed at on the server
//   3. convert — applied straight away, or held for approval if the project
//      has requireProposalApproval set
//
// Approval is NOT handled here. Batches awaiting approval appear in the
// existing Review Center (/approval-queue) under "Minutes to apply", alongside
// the proposal, triage and checklist queues — one place to review things
// rather than a second queue nobody remembers to open.
import React, { useEffect, useMemo, useState } from "react";
import { filterPeopleOption, renderPeopleOption } from "components/PeoplePicker/PeoplePicker";
import { peoplePickerOptions, emailValue } from "components/PeoplePicker/peoplePickerOptions";
import { useHistory } from "react-router-dom";
import {
  Alert, Button, Card, Input, Select, Space, Steps,
  Table, Tag, Tooltip, Upload, message,
} from "antd";
import { activityLabel, activityTitle, withActivityPaths } from "helpers/schedule/activityLabel";
import { InboxOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import useLastProject from "components/Coordination/useLastProject";
import useProjectAccess from "hooks/useProjectAccess";
import { labelFor } from "helpers/artifactPrefix";
import { matchOption } from "components/SearchSelect/searchSelect";

const { TextArea } = Input;

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` };
}

const api = async (path, options = {}) => {
  const res = await fetch(`${url}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...auth(), ...(options.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
  return body;
};

// multipart: no Content-Type header, the browser sets the boundary.
const apiUpload = async (path, formData) => {
  const res = await fetch(`${url}${path}`, { method: "POST", headers: auth(), body: formData });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
  return body;
};

const KIND_COLOR = { issue: "red", checklist: "blue", milestone: "purple", task: "default" };
const OP_COLOR = { create: "green", update: "gold", delete: "volcano" };
const KINDS = ["issue", "checklist", "milestone", "task"];
const OPS = ["create", "update", "delete"];

const dateStr = (d) => (d ? String(d).slice(0, 10) : "");

// Every property of a proposed row worth searching on, for long minutes
// where the list runs past a screen. "All properties" is the default.
const ROW_COLUMNS = [
  { value: "all", label: "All properties" },
  { value: "title", label: "Title" },
  { value: "description", label: "Description" },
  { value: "assignee", label: "Owner" },
  { value: "kind", label: "As (issue / checklist / …)" },
  { value: "op", label: "Do (create / update / delete)" },
  { value: "discipline", label: "Discipline" },
  { value: "priority", label: "Priority" },
  { value: "dueDate", label: "Due date" },
  { value: "sourceLine", label: "Original line" },
];

export default function MinutesToPlanPage() {
  const history = useHistory();
  const [projects, setProjects] = useState([]);
  // Remembers the last project (shared with the other coordination pages).
  const [projectId, setProjectId] = useLastProject(projects);
  const [planItems, setPlanItems] = useState([]);
  const [ganttItemId, setGanttItemId] = useState("");

  const [text, setText] = useState("");
  const [file, setFile] = useState(null);

  const [parsed, setParsed] = useState(null);      // { items, attendees, ... }
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Everyone on the project, grouped by company - who Owner can be.
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [ownerSearch, setOwnerSearch] = useState("");
  // Review-table search: the whole row, or one property.
  const [rowSearch, setRowSearch] = useState("");
  const [rowColumn, setRowColumn] = useState("all");

  const step = result ? 2 : parsed ? 1 : 0;
  // Converting minutes writes plan items + issues: owner-company side with
  // minutesAdd + ganttAdd; partner companies get a read-only notice.
  const { can, access } = useProjectAccess(projectId);
  const canConvert = !!(can.minutesAdd && can.ganttAdd);
  // Applied at once (no approval step), each ticked row needs its own kind's
  // key for its operation - the server refuses the batch otherwise
  // (routes/minutesConvert OP_KEYS, 2026-10-14). Named here before the click.
  const OP_CAN = {
    issue: { create: "issueCreate", update: "issueEdit", delete: "issueDelete" },
    checklist: { create: "checklistAdd", update: "checklistEdit", delete: "checklistDelete" },
    milestone: { create: "ganttAdd", update: "ganttEdit", delete: "ganttDelete" },
    task: { create: "ganttAdd", update: "ganttEdit", delete: "ganttDelete" },
  };
  const missingOpKeys = parsed?.requiresApproval ? [] : [...new Set(rows
    .filter((r) => selected.includes(r.key))
    .map((r) => (OP_CAN[r.kind] || OP_CAN.task)[r.op || "create"])
    .filter((k) => k && !can[k]))];

  useEffect(() => {
    api("/projects")
      .then((r) => setProjects(Array.isArray(r) ? r : r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!projectId) return;
    setGanttItemId("");
    setPlanItems([]);
    api(`/projects/${projectId}/gantt-items`)
      .then((r) => setPlanItems((Array.isArray(r) ? r : r.data || []).filter((g) => !g.deleted)))
      .catch(() => setPlanItems([]));
    setRosterLoading(true);
    api(`/projects/${projectId}/participants/roster`)
      .then((r) => setRoster(Array.isArray(r) ? r : []))
      .catch(() => setRoster([]))
      .finally(() => setRosterLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Owner options: grouped by company, valued by EMAIL - that address is
  // the only thing that assigns anybody when the batch is applied.
  const ownerGroups = useMemo(
    // The shared picker's options (components/PeoplePicker, owner
    // 2026-10-15). routes/minutesConvert resolveAssignee finds an EXISTING
    // account by address and nothing else, so a roster contact with no
    // account would be silently left unassigned: listed greyed out, with
    // the reason, instead. A partner executive likewise - a reader.
    () => peoplePickerOptions(roster, { personValue: emailValue, noAccount: "disable", executives: "disable" })
      .map((g) => ({ ...g, options: g.options.map((o) => ({ ...o, name: (o.person && o.person.name) || o.label })) })),
    [roster]
  );
  const ownerPeople = useMemo(
    () => ownerGroups.reduce((acc, g) => acc.concat(g.options.filter((o) => !o.disabled)), []),
    [ownerGroups]
  );
  // The parser reads "@Diego" out of the prose. Offer the one person that
  // can be, when there IS exactly one - shown in the picker, so what gets
  // assigned is what somebody looked at and kept.
  const matchOwner = (name) => {
    const n = String(name || "").trim().toLowerCase();
    if (!n) return "";
    const hits = ownerPeople.filter((o) => {
      const full = o.name.toLowerCase();
      return o.value === n || full === n || full.split(/\s+/)[0] === n;
    });
    return hits.length === 1 ? hits[0].value : "";
  };

  const doParse = async () => {
    if (!projectId) return message.warning("Pick a project first");
    if (!file && !text.trim()) return message.warning("Paste your minutes or attach a file");
    setBusy(true);
    try {
      let res;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        res = await apiUpload(`/projects/${projectId}/minutes/parse`, fd);
      } else {
        res = await api(`/projects/${projectId}/minutes/parse`, {
          method: "POST",
          body: JSON.stringify({ text }),
        });
      }
      if (!res.items?.length) {
        message.warning("Nothing in there looked like an action item");
      }
      setParsed(res);
      setRows(res.items.map((it) => ({
        ...it,
        key: it.index,
        // Empty means "use the line it came from", which is what the
        // converter wrote on its own before this column existed.
        description: it.description || "",
        assigneeEmail: matchOwner(it.assignee),
      })));
      setSelected(res.items.map((it) => it.index));
      setRowSearch(""); setRowColumn("all");
      setResult(null);
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const patchRow = (key, patch) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const doConvert = async () => {
    if (!ganttItemId) {
      return message.warning("Pick the schedule activity these minutes belong to — that is what the work attaches to");
    }
    const items = rows.filter((r) => selected.includes(r.key));
    if (!items.length) return message.warning("Nothing ticked");
    setBusy(true);
    try {
      const res = await api(`/projects/${projectId}/minutes/convert`, {
        method: "POST",
        body: JSON.stringify({
          ganttItemId,
          items,
          attendees: parsed?.attendees || [],
          meetingDate: parsed?.meetingDate || null,
          occurrenceDate: parsed?.meetingDate || new Date().toISOString(),
          sourceText: text.slice(0, 20000),
          sourceFilename: parsed?.sourceFilename || (file ? file.name : ""),
        }),
      });
      setResult(res);
      if (res.pendingApproval) {
        message.success("Held for approval — see the Review Center");
      } else {
        message.success(`${res.applied} item(s) added to the plan`);
      }
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setParsed(null); setRows([]); setSelected([]); setResult(null); setText(""); setFile(null);
  };

  // THE PARENT PATH, NOT JUST THE NAME. Owner, 2026-10-01: "make sure all
  // activities have the sequence from parent to child using the ... for long
  // names, and make sure the tool tip shows everything". A bare name puts
  // "Area A" in the list nine times on RT06 with nothing to tell them apart.
  // Paths derived from the FULL list before any filter - the helper stops at
  // an unresolvable parent rather than joining across it.
  const planWithPaths = useMemo(() => withActivityPaths(planItems), [planItems]);
  const planLabel = (g) =>
    `${g.wbs ? g.wbs + " " : ""}${activityLabel(g) || g.name}${g.type && g.type !== "task" ? ` (${g.type})` : ""}`;
  const planTitle = (g) =>
    [activityTitle(g) || g.name, g.wbs, g.type && g.type !== "task" ? g.type : ""].filter(Boolean).join(" · ");

  const columns = useMemo(
    () => [
      {
        title: "Do", dataIndex: "op", width: 110,
        render: (v, r) => (
          <Select size="small" value={v} style={{ width: 96 }}
            onChange={(x) => patchRow(r.key, { op: x })}
            options={OPS.map((o) => ({ value: o, label: <Tag color={OP_COLOR[o]}>{o}</Tag> }))} />
        ),
      },
      {
        title: "As", dataIndex: "kind", width: 130,
        render: (v, r) => (
          <Select size="small" value={v} style={{ width: 116 }}
            onChange={(x) => patchRow(r.key, { kind: x })}
            options={KINDS.map((k) => ({ value: k, label: <Tag color={KIND_COLOR[k]}>{k}</Tag> }))} />
        ),
      },
      {
        title: "Title", dataIndex: "title",
        render: (v, r) => (
          <Tooltip title={r.sourceLine} placement="topLeft">
            <Input size="small" value={v} onChange={(e) => patchRow(r.key, { title: e.target.value })} />
          </Tooltip>
        ),
      },
      {
        title: "Ref", dataIndex: "ref", width: 80,
        render: (v, r) => (v?.displayId ? <Tag>{labelFor(v.displayId, v.artifactPrefix || r?.artifactPrefix)}</Tag> : <span style={{ color: "#bbb" }}>—</span>),
      },
      {
        // The issue's opening note / the checklist item's notes. Left
        // empty, the sentence from the minutes is used, as before.
        title: "Description", dataIndex: "description", width: 260,
        render: (v, r) => (
          <Input.TextArea size="small" value={v} autoSize={{ minRows: 1, maxRows: 4 }}
            placeholder={r.sourceLine || "What this is, and who should act"}
            onChange={(e) => patchRow(r.key, { description: e.target.value })} />
        ),
      },
      {
        // Picked from the project, not typed: an email is what actually
        // assigns the work when the batch is applied. The parsed name
        // stays visible as the placeholder when nobody matched it.
        title: "Owner", dataIndex: "assigneeEmail", width: 210,
        render: (v, r) => (
          <Select
            size="small" allowClear showSearch style={{ width: "100%" }}
            value={v || undefined}
            placeholder={rosterLoading ? "loading people…" : (r.assignee || "nobody")}
            status={r.assignee && !v ? "warning" : undefined}
            filterOption={filterPeopleOption}
            optionRender={renderPeopleOption}
            notFoundContent={rosterLoading ? "loading…" : "No match — type a full email"}
            onSearch={(txt) => setOwnerSearch(txt)}
            onBlur={() => {
              if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerSearch) && ownerSearch !== v) {
                patchRow(r.key, { assigneeEmail: ownerSearch.toLowerCase() });
              }
            }}
            options={ownerGroups}
            onChange={(val, opt) => patchRow(r.key, {
              assigneeEmail: val || "",
              assignee: val ? (opt?.name || r.assignee || "") : r.assignee || "",
            })}
          />
        ),
      },
      {
        title: "Due", dataIndex: "dueDate", width: 140,
        render: (v, r) => (
          <Input size="small" type="date" value={dateStr(v)}
            onChange={(e) => patchRow(r.key, { dueDate: e.target.value || null })} />
        ),
      },
      {
        title: "Priority", dataIndex: "priority", width: 110,
        render: (v, r) => (
          <Select size="small" value={v} style={{ width: 96 }}
            onChange={(x) => patchRow(r.key, { priority: x })}
            options={["urgent", "normal", "low"].map((p) => ({ value: p, label: p }))} />
        ),
      },
      {
        title: "Discipline", dataIndex: "discipline", width: 130,
        render: (v, r) => (
          <Input size="small" value={v} placeholder="—"
            onChange={(e) => patchRow(r.key, { discipline: e.target.value })} />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ownerGroups, rosterLoading, ownerSearch]
  );

  // Rows shown on the review table. Ticking is unaffected by the filter:
  // hidden rows keep their tick, which is why the count is spelled out.
  const visibleRows = useMemo(() => {
    const q = rowSearch.trim().toLowerCase();
    if (!q) return rows;
    const read = (r) => {
      switch (rowColumn) {
        case "all": return [r.title, r.description, r.assignee, r.assigneeEmail, r.kind, r.op, r.discipline, r.priority, dateStr(r.dueDate), r.sourceLine];
        case "dueDate": return [dateStr(r.dueDate)];
        case "assignee": return [r.assignee, r.assigneeEmail];
        default: return [r[rowColumn]];
      }
    };
    return rows.filter((r) => read(r).filter(Boolean).some((v) => {
      const t = String(v).toLowerCase();
      return rowColumn === "dueDate" ? t.startsWith(q) : t.includes(q);
    }));
  }, [rows, rowSearch, rowColumn]);

  return (
    <div className="w-100 p-3">
      <CoordinationBreadcrumbs current="Minutes to plan" />

      <h3 className="mb-1">Minutes to plan</h3>
      <p style={{ color: "#6b7a86" }}>
        Paste your meeting minutes or attach the file. Everything you keep is
        created against the schedule activity you choose, so it lands on the schedule
        and shows up in the daily digest.
      </p>

      <Steps
        size="small"
        current={step}
        className="mb-3"
        items={[{ title: "Minutes" }, { title: "Review" }, { title: "Done" }]}
      />

      <Card size="small" className="mb-3">
        <Space wrap size="middle">
          <Select filterOption={matchOption}
            showSearch style={{ minWidth: 260 }} placeholder="Project"
            value={projectId || undefined} onChange={setProjectId}
            optionFilterProp="label"
            options={projects.map((p) => ({ value: p._id, label: p.title || "(untitled)" }))}
          />
          <Select filterOption={matchOption}
            showSearch style={{ minWidth: 340 }}
            placeholder="Schedule activity these minutes belong to"
            value={ganttItemId || undefined} onChange={setGanttItemId}
            optionFilterProp="label" disabled={!projectId}
            options={planWithPaths.map((g) => ({ value: g._id, label: planLabel(g), title: planTitle(g) }))}
          />
        </Space>
        {projectId && access && !canConvert && (
          <Alert type="warning" showIcon style={{ marginTop: 8 }}
                 message="View only: converting minutes into schedule activities needs the minutesAdd and ganttAdd permissions on the project-owner side." />
        )}
        {!ganttItemId && projectId && (
          <div style={{ marginTop: 8 }}>
            <Alert
              type="info" showIcon
              message="Pick a schedule activity — issues and checklists attach to it, and milestones and tasks become rows underneath it on the schedule."
            />
          </div>
        )}
      </Card>

      {step === 0 && (
        <Card size="small" title="Minutes">
          <TextArea
            rows={10} value={text} onChange={(e) => setText(e.target.value)}
            placeholder={
              "Paste minutes here, e.g.\n\nAttendees: Sarah Chen, Diego Salas\nDate: 2026-08-20\n\n- Clash: supply duct conflicts with sprinkler main above corridor L2 @Diego due 2026-09-01 urgent\n- Verify the fire caulk at L2 shaft penetrations\n- Milestone: permit approval by 9/15/26\n- Update BLT-126 due date to 2026-10-01"
            }
          />
          <div className="mt-2">
            <Upload.Dragger
              multiple={false} maxCount={1}
              accept=".txt,.md,.markdown,.csv,.tsv,.xml,.json,.docx,.log"
              beforeUpload={(f) => { setFile(f); return false; }}
              onRemove={() => setFile(null)}
              fileList={file ? [file] : []}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">or drop a file</p>
              <p className="ant-upload-hint">txt, md, csv, tsv, xml, json, docx</p>
            </Upload.Dragger>
          </div>
          <div className="mt-3">
            <Button type="primary" icon={<ThunderboltOutlined />} loading={busy} onClick={doParse}
                    disabled={!!projectId && !!access && !canConvert}>
              Read the minutes
            </Button>
          </div>
        </Card>
      )}

      {step === 1 && (
        <Card
          size="small"
          title={`Review — ${rows.length} item(s) found`}
          extra={
            <Space>
              {parsed?.requiresApproval && <Tag color="gold">needs approval</Tag>}
              <Button size="small" onClick={reset}>Start over</Button>
            </Space>
          }
        >
          {parsed?.attendees?.length > 0 && (
            <p style={{ color: "#6b7a86" }}>
              Attendees: {parsed.attendees.join(", ")}
              {parsed.meetingDate ? ` · ${dateStr(parsed.meetingDate)}` : ""}
            </p>
          )}
          <Alert
            className="mb-2" type="warning" showIcon
            message="Check these before converting. Untick anything that is not real work, and correct anything the parser read wrongly — hover a title to see the original line."
          />
          <Space wrap style={{ marginBottom: 8 }}>
            <Input.Search allowClear style={{ width: 320 }} value={rowSearch}
              placeholder={rowColumn === "dueDate"
                ? "YYYY, YYYY-MM or YYYY-MM-DD"
                : rowColumn === "all"
                  ? "Search these rows (title, owner, discipline, original line…)"
                  : `Search ${(ROW_COLUMNS.find((c) => c.value === rowColumn) || {}).label}`}
              onChange={(e) => setRowSearch(e.target.value)} />
            <Select value={rowColumn} onChange={setRowColumn} style={{ width: 210 }}
              options={ROW_COLUMNS} title="Search one property instead of the whole row" />
            <span style={{ color: "#888", fontSize: 12 }}>
              {visibleRows.length} of {rows.length} shown · {selected.length} ticked
            </span>
            {(rowSearch || rowColumn !== "all") && (
              <Button size="small" type="text" onClick={() => { setRowSearch(""); setRowColumn("all"); }}>
                Clear
              </Button>
            )}
            <Button size="small" onClick={() => setSelected(visibleRows.map((r) => r.key))}
                    disabled={!visibleRows.length}>Tick shown only</Button>
          </Space>
          <Table
            size="small" rowKey="key" columns={columns} dataSource={visibleRows}
            pagination={false} scroll={{ x: 1500 }}
            rowSelection={{ selectedRowKeys: selected, onChange: setSelected }}
          />
          <div className="mt-3">
            <Button
              type="primary" loading={busy} onClick={doConvert}
              disabled={!selected.length || !canConvert || missingOpKeys.length > 0}
            >
              {parsed?.requiresApproval
                ? `Submit ${selected.length} item(s) for approval`
                : `Add ${selected.length} item(s) to the plan`}
            </Button>
          </div>
          {missingOpKeys.length > 0 && (
            <Alert type="warning" showIcon style={{ marginTop: 8 }}
                   message={`Some ticked rows need a permission you do not hold: ${missingOpKeys.join(", ")}. Untick them, or ask a company administrator.`} />
          )}
        </Card>
      )}

      {step === 2 && result && (
        <Card size="small" title="Done" extra={<Button size="small" onClick={reset}>Import more</Button>}>
          {result.pendingApproval ? (
            <Alert
              type="info" showIcon
              message="Held for approval"
              description="This project requires changes to be approved. The batch is waiting in the Review Center under 'Minutes'."
            />
          ) : (
            <Alert
              type="success" showIcon
              message={`${result.applied} item(s) added to the plan`}
              description={
                <>
                  <div>
                    {result.failed?.length
                      ? `${result.failed.length} could not be applied: ${result.failed
                          .map((f) => `${f.title} (${f.error})`)
                          .join("; ")}`
                      : `All items landed on ${result.planItem?.name || "the schedule activity you chose"}.`}
                  </div>
                  {/* Say where they went, and carry the project - a list that
                      opens on some other remembered project makes new work
                      look like it was never created. */}
                  <Space wrap style={{ marginTop: 8 }}>
                    {result.createdByKind?.issue > 0 && (
                      <Button size="small" type="primary"
                              onClick={() => history.push(`/issues?projectId=${projectId}&createdToday=true`)}>
                        View these {result.createdByKind.issue} issue(s)
                      </Button>
                    )}
                    {result.createdByKind?.checklist > 0 && (
                      <Button size="small"
                              onClick={() => history.push(`/checklists?projectId=${projectId}&createdToday=1`)}>
                        View these {result.createdByKind.checklist} checklist item(s)
                      </Button>
                    )}
                    {(result.createdByKind?.milestone > 0 || result.createdByKind?.task > 0) && (
                      <Button size="small"
                              onClick={() => history.push(`/coordination/${projectId}${result.planItem?._id ? `?focus=${result.planItem._id}` : ""}`)}>
                        Open the schedule
                      </Button>
                    )}
                  </Space>
                </>
              }
            />
          )}
        </Card>
      )}

      {parsed?.requiresApproval && (
        <Alert
          className="mt-3" type="info" showIcon
          message="This project requires approval"
          description={
            <span>
              Submitted batches wait in the{" "}
              <a href="/approval-queue">Review Center</a> under
              &ldquo;Minutes&rdquo;. Nothing reaches the plan until
              somebody approves it there.
            </span>
          }
        />
      )}
    </div>
  );
}
